import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Shield, LogOut } from "lucide-react";
import { useState } from "react";

const links = [
  { to: "/", label: "Home", id: "landing" },
  { to: "/console", label: "Console", id: "console" },
  { to: "/tools", label: "Free Tools", id: "tools" },
  { to: "/docs", label: "Docs", id: "docs" },
  { to: "/pricing", label: "Pricing", id: "pricing" },
  { to: "/compare", label: "Compare", id: "compare" },
  { to: "/launch", label: "Launch", id: "launch" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0d14]/80 border-b border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_-2px_rgba(0,240,255,0.6)]">
            <Shield className="w-4 h-4 text-slate-950" strokeWidth={2.75} fill="currentColor" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">
            The Good Guys<span className="text-cyan-signal">.</span>
          </span>
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-slate-500 ml-2 border border-slate-800 px-1.5 py-0.5 rounded">v1</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                loc.pathname === l.to
                  ? "text-cyan-signal bg-cyan-500/5"
                  : "text-slate-400 hover:text-white hover:bg-slate-900"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-link-dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-900">
                  {user.name || user.email.split("@")[0]}
                </Button>
              </Link>
              <Button
                data-testid="nav-btn-logout"
                variant="ghost"
                size="sm"
                onClick={() => { logout(); nav("/"); }}
                className="text-slate-400 hover:text-white hover:bg-slate-900"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-btn-login">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">Sign in</Button>
              </Link>
              <Link to="/register" data-testid="nav-btn-signup">
                <Button size="sm" className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">
                  Get API Key
                </Button>
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-btn-mobile-toggle"
          className="md:hidden p-2 text-slate-300"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-800 bg-[#0a0d14]">
          <div className="px-4 py-3 space-y-1">
            {links.map((l) => (
              <Link
                key={l.id}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="block px-3 py-2 text-sm text-slate-300 hover:bg-slate-900 rounded-md"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-2 flex gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="flex-1">
                    <Button size="sm" variant="secondary" className="w-full">Dashboard</Button>
                  </Link>
                  <Button size="sm" onClick={() => { logout(); setOpen(false); nav("/"); }} className="flex-1">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1">
                    <Button size="sm" variant="secondary" className="w-full">Sign in</Button>
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="flex-1">
                    <Button size="sm" className="w-full bg-cyan-signal text-slate-950 hover:bg-cyan-400">Sign up</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
